import {
  AddressFormikType,
  AddressInputKey,
  convertAddressFromFormik,
  convertAddressToFormik,
} from "@modules/address/addressFormikType"
import { BoxDraft } from "@modules/box/boxTypes"
import { word } from "@modules/core/utils/i18n"
import { HUImage } from "@modules/types/imageTypes"
import { LocationDto } from "@modules/types/locationTypes"
import parsePhoneNumber, { CountryCode } from "libphonenumber-js"
import * as Yup from "yup"

export type BoxInputKey =
  | "name"
  | "email"
  | "phoneNumberCountryCode"
  | "phoneNumber"
  | "siren"
  | "description"
  | "logo"
  | "cover"
  | "boxPhotos"
  | "location"
  | "slotGeneratorInWeeks"
  | "zoneId"
  | AddressInputKey

export type BoxFormikType = {
  name: string
  description: string
  phoneNumberCountryCode: string
  phoneNumber: string
  email: string
  siren: string
  logo?: HUImage
  cover?: HUImage
  boxPhotos: HUImage[]
  location?: LocationDto
  slotGeneratorInWeeks: number
  zoneId?: string
} & AddressFormikType

export function convertBoxToFormik(boxDraft: BoxDraft): BoxFormikType {
  return {
    name: boxDraft.name,
    description: boxDraft.description,
    ...convertAddressToFormik(boxDraft.address),
    phoneNumber: boxDraft.phoneNumberWithPrefix.number,
    phoneNumberCountryCode: boxDraft.phoneNumberWithPrefix.countryCode,
    email: boxDraft.email,
    siren: boxDraft.siren,
    logo: boxDraft.logo,
    cover: boxDraft.cover,
    boxPhotos: boxDraft.boxPhotos,
    location: boxDraft.location,
    slotGeneratorInWeeks: boxDraft.slotGeneratorInWeeks ?? 2,
    zoneId: boxDraft.zoneId,
  }
}

export function convertBoxFormFormik(formik: BoxFormikType): BoxDraft {
  const parse =
    formik.phoneNumber &&
    formik.phoneNumberCountryCode &&
    parsePhoneNumber(formik.phoneNumber, formik.phoneNumberCountryCode as CountryCode)

  return {
    name: formik.name,
    description: formik.description,
    address: convertAddressFromFormik(formik),
    phoneNumberWithPrefix: parse
      ? {
          number: parse.nationalNumber,
          countryCode: formik.phoneNumberCountryCode,
        }
      : {
          number: formik.phoneNumber,
          countryCode: formik.phoneNumberCountryCode,
        },
    email: formik.email,
    siren: formik.siren,
    logo: formik.logo,
    cover: formik.cover,
    boxPhotos: formik.boxPhotos,
    location: formik.location,
    slotGeneratorInWeeks: formik.slotGeneratorInWeeks ?? 2,
    zoneId: formik.zoneId,
  }
}

const firstPageValidation = () =>
  Yup.object({
    name: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .required(word("global.form.required")),
  })

const secondPageValidation = (timezones: string[]) =>
  Yup.object({
    address: Yup.string()
      .max(255, word("form.maximum.characters", { count: 255 }))
      .required(word("global.form.required")),
    additionalAddress: Yup.string().max(255, word("form.maximum.characters", { count: 255 })),
    postalCode: Yup.string()
      .required(word("global.form.required"))
      .max(10, word("form.maximum.characters", { count: 10 })),
    city: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .required(word("global.form.required")),
    country: Yup.string()
      .max(255, word("form.maximum.characters", { count: 255 }))
      .required(word("global.form.required")),
    zoneId: Yup.string().required(word("form.required.timezones")).oneOf(timezones, word("form.required.timezones")),
  })

const thirdPageValidation = () =>
  Yup.object({
    email: Yup.string()
      .required(word("global.form.required"))
      .max(64, word("form.maximum.characters", { count: 64 }))
      .email(word("box.form.email.error")),
    phoneNumber: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .test("phoneNumber", word("box.form.phone.error"), (value, context) => {
        if (value) {
          const parse = parsePhoneNumber(value, context.parent.phoneNumberCountryCode as CountryCode)
          return !!parse?.isValid()
        }
        return false // if no value, return false
      }),
    phoneNumberCountryCode: Yup.string().max(2, word("form.maximum.characters", { count: 2 })),
  })

const fourthPageValidation = () =>
  Yup.object({
    logo: Yup.mixed().required(word("box.form.logo.error")),
    cover: Yup.mixed().required(word("box.form.cover.error")),
  })

const fifthPageValidation = () => Yup.object({ description: Yup.string().required(word("global.form.required")) })

const sixthPageValidation = () =>
  Yup.object({
    siren: Yup.string()
      .max(16, word("form.maximum.characters", { count: 16 }))
      .required(word("global.form.required")),
  })

const boxAdminPageSpecificValidation = () =>
  Yup.object({
    slotGeneratorInWeeks: Yup.number()
      .required(word("global.form.required"))
      .min(2, word("global.form.minimumValue", { minValue: 2 }))
      .max(52, word("global.form.maximumValue", { maxValue: 52 })),
  })

export function getValidationSchema(pageNumber?: number) {
  const timezones = Intl.supportedValuesOf("timeZone")

  switch (pageNumber) {
    case 1:
      return firstPageValidation()
    case 2:
      return secondPageValidation(timezones)
    case 3:
      return thirdPageValidation()
    case 4:
      return fourthPageValidation()
    case 5:
      return fifthPageValidation()
    case 6:
      return sixthPageValidation()
    default:
      return firstPageValidation()
        .concat(secondPageValidation(timezones))
        .concat(thirdPageValidation())
        .concat(fourthPageValidation())
        .concat(fifthPageValidation())
        .concat(sixthPageValidation())
        .concat(boxAdminPageSpecificValidation())
  }
}
